import React, { useState } from 'react';
import { Box, Typography, Paper, Button, CircularProgress, Alert, TextField } from '@mui/material';
import { Shield, Mail, AlertTriangle } from 'lucide-react';
import { useAuth0 } from '@auth0/auth0-react';
import { API_URLS } from '../../config';

interface DomainVerificationRequiredProps {
  companyName: string;
  companyDomain: string;
  userEmail: string;
}

export const DomainVerificationRequired: React.FC<DomainVerificationRequiredProps> = ({
  companyName,
  companyDomain,
  userEmail,
}) => {
  const { user, logout, getAccessTokenSilently } = useAuth0();

  // Pre-fill email if user's email domain matches company domain
  const userEmailDomain = userEmail.split('@')[1]?.toLowerCase();
  const expectedDomain = companyDomain.toLowerCase();
  const initialEmail = userEmailDomain === expectedDomain ? userEmail : '';

  const [verificationEmail, setVerificationEmail] = useState(initialEmail);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSendVerification = async () => {
    // Validate email domain
    const emailDomain = verificationEmail.split('@')[1]?.toLowerCase();

    if (!emailDomain) {
      setError('Please enter a valid email address');
      return;
    }

    if (emailDomain !== expectedDomain) {
      setError(`Email must be from ${expectedDomain} domain`);
      return;
    }

    setSending(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(`${API_URLS.BASE}/auth/send-domain-verification`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          user_id: user?.sub,
          verification_email: verificationEmail,
          company_name: companyName,
          company_domain: companyDomain,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to send verification email');
      }

      setSent(true);
    } catch (err) {
      console.error('Error sending domain verification:', err);
      setError(err instanceof Error ? err.message : 'Failed to send verification email');
    } finally {
      setSending(false);
    }
  };

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } });
  };

  if (sent) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#f5f5f5',
          p: 3,
        }}
      >
        <Paper
          sx={{
            maxWidth: 500,
            width: '100%',
            p: 4,
            borderRadius: 3,
            textAlign: 'center',
          }}
        >
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: '#E8F5E9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mx: 'auto',
              mb: 3,
            }}
          >
            <Mail size={40} color="#4CAF50" />
          </Box>

          <Typography variant="h5" fontWeight={600} gutterBottom>
            Verification Email Sent!
          </Typography>

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            We've sent a verification link to <strong>{verificationEmail}</strong>
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Please check your inbox and click the verification link to confirm your employment
            at {companyName}. After verifying, log in again to access your account.
          </Typography>

          <Button variant="contained" onClick={handleLogout} fullWidth>
            Logout
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: '#f5f5f5',
        p: 3,
      }}
    >
      <Paper
        sx={{
          maxWidth: 550,
          width: '100%',
          p: 4,
          borderRadius: 3,
        }}
      >
        <Box
          sx={{
            width: 80,
            height: 80,
            borderRadius: '50%',
            bgcolor: '#FFF3E0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 3,
          }}
        >
          <Shield size={40} color="#F57C00" />
        </Box>

        <Typography variant="h5" fontWeight={600} gutterBottom textAlign="center">
          Domain Verification Required
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
          To ensure you're a legitimate recruiter from {companyName}, we need to verify
          your company email address by sending a verification link.
        </Typography>

        {userEmail.split('@')[1]?.toLowerCase() !== companyDomain.toLowerCase() ? (
          <Alert severity="warning" sx={{ mb: 3 }} icon={<AlertTriangle size={20} />}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              Email Domain Mismatch Detected
            </Typography>
            <Typography variant="body2">
              Your login email (<strong>{userEmail}</strong>) doesn't match the domain for{' '}
              {companyName} (<strong>{companyDomain}</strong>).
            </Typography>
          </Alert>
        ) : (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              Your login email (<strong>{userEmail}</strong>) matches {companyName}'s domain.
              Please verify your email to confirm you have access to this address.
            </Typography>
          </Alert>
        )}

        <Typography variant="body2" fontWeight={600} sx={{ mb: 1 }}>
          Enter your company email address
        </Typography>

        <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
          Must be from @{companyDomain}
        </Typography>

        <TextField
          fullWidth
          label="Company Email"
          placeholder={`yourname@${companyDomain}`}
          value={verificationEmail}
          onChange={(e) => setVerificationEmail(e.target.value)}
          disabled={sending}
          sx={{ mb: 2 }}
          helperText={`We'll send a verification link to this address`}
        />

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Button
            variant="contained"
            onClick={handleSendVerification}
            disabled={sending || !verificationEmail}
            fullWidth
            startIcon={sending && <CircularProgress size={18} />}
          >
            {sending ? 'Sending...' : 'Send Verification Email'}
          </Button>

          <Button variant="outlined" onClick={handleLogout} fullWidth>
            Logout
          </Button>
        </Box>

        <Typography variant="caption" color="text.secondary" sx={{ mt: 3, display: 'block', textAlign: 'center' }}>
          This verification step prevents fraudulent recruiters from posing as employees
          of companies they don't work for.
        </Typography>
      </Paper>
    </Box>
  );
};
